import React, { useCallback, useRef, useState } from 'react'

interface Point {
  x: number
  y: number
}

interface Gesture {
  start: Point
  startOffset: Point
  startScale: number
  startDistance: number
  moved: boolean
}

interface Options {
  onSwipe: (direction: 1 | -1) => void
}

const MIN_SCALE = 1
const MAX_SCALE = 4
const DOUBLE_TAP_SCALE = 2.5
const DOUBLE_TAP_MS = 300
const SWIPE_PX = 60
const ORIGIN: Point = { x: 0, y: 0 }

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y)

/**
 * Pinch / wheel / double-tap zoom with drag-to-pan for the lightbox stage.
 * At 1× a horizontal swipe changes image instead of panning.
 */
export function useZoomPan(
  stageRef: React.RefObject<HTMLElement>,
  imageRef: React.RefObject<HTMLImageElement>,
  { onSwipe }: Options,
) {
  const [scale, setScale] = useState(MIN_SCALE)
  const [offset, setOffset] = useState<Point>(ORIGIN)
  const [interacting, setInteracting] = useState(false)
  const scaleRef = useRef(MIN_SCALE)
  const offsetRef = useRef<Point>(ORIGIN)
  const pointers = useRef(new Map<number, Point>())
  const gesture = useRef<Gesture | null>(null)
  const lastTap = useRef(0)

  const clampOffset = useCallback(
    (next: Point, s: number): Point => {
      const stage = stageRef.current
      const image = imageRef.current
      if (!stage || !image) return ORIGIN
      const maxX = Math.max(0, (image.offsetWidth * s - stage.clientWidth) / 2)
      const maxY = Math.max(0, (image.offsetHeight * s - stage.clientHeight) / 2)
      return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) }
    },
    [stageRef, imageRef],
  )

  const commit = useCallback(
    (s: number, o: Point) => {
      let nextScale = clamp(s, MIN_SCALE, MAX_SCALE)
      if (nextScale < 1.02) nextScale = MIN_SCALE
      const nextOffset = nextScale === MIN_SCALE ? ORIGIN : clampOffset(o, nextScale)
      scaleRef.current = nextScale
      offsetRef.current = nextOffset
      setScale(nextScale)
      setOffset(nextOffset)
    },
    [clampOffset],
  )

  const reset = useCallback(() => commit(MIN_SCALE, ORIGIN), [commit])
  const zoomIn = useCallback(() => commit(scaleRef.current * 1.5, offsetRef.current), [commit])
  const zoomOut = useCallback(() => commit(scaleRef.current / 1.5, offsetRef.current), [commit])
  const toggle = useCallback(
    () => (scaleRef.current > MIN_SCALE ? commit(MIN_SCALE, ORIGIN) : commit(DOUBLE_TAP_SCALE, ORIGIN)),
    [commit],
  )

  const startGesture = (start: Point, moved: boolean) => {
    const pts = Array.from(pointers.current.values())
    gesture.current = {
      start,
      startOffset: offsetRef.current,
      startScale: scaleRef.current,
      startDistance: pts.length >= 2 ? distance(pts[0], pts[1]) : 0,
      moved,
    }
  }

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    setInteracting(true)
    startGesture({ x: event.clientX, y: event.clientY }, pointers.current.size > 1)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const g = gesture.current
    if (!g) return

    const pts = Array.from(pointers.current.values())
    if (pts.length >= 2 && g.startDistance > 0) {
      g.moved = true
      commit((g.startScale * distance(pts[0], pts[1])) / g.startDistance, g.startOffset)
      return
    }

    const dx = event.clientX - g.start.x
    const dy = event.clientY - g.start.y
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) g.moved = true
    if (scaleRef.current > MIN_SCALE) {
      commit(scaleRef.current, { x: g.startOffset.x + dx, y: g.startOffset.y + dy })
    }
  }

  const onPointerEnd = (event: React.PointerEvent<HTMLElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    const g = gesture.current
    const wasLast = pointers.current.size === 1
    pointers.current.delete(event.pointerId)

    if (!wasLast) {
      const [rest] = Array.from(pointers.current.values())
      startGesture(rest, true)
      return
    }

    setInteracting(false)
    gesture.current = null
    if (!g) return

    const dx = event.clientX - g.start.x
    const dy = event.clientY - g.start.y

    if (!g.moved) {
      const now = performance.now()
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0
        toggle()
      } else {
        lastTap.current = now
      }
      return
    }

    if (
      g.startScale === MIN_SCALE &&
      scaleRef.current === MIN_SCALE &&
      Math.abs(dx) > SWIPE_PX &&
      Math.abs(dx) > Math.abs(dy) * 1.2
    ) {
      onSwipe(dx < 0 ? 1 : -1)
    }
  }

  const onWheel = (event: React.WheelEvent<HTMLElement>) => {
    commit(scaleRef.current * (event.deltaY < 0 ? 1.12 : 1 / 1.12), offsetRef.current)
  }

  return {
    scale,
    offset,
    interacting,
    zoomIn,
    zoomOut,
    reset,
    canZoomIn: scale < MAX_SCALE,
    canZoomOut: scale > MIN_SCALE,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onWheel,
    },
  }
}
